/**
 * Auth service: token endpoint (password grant). No Bearer header on the token request.
 * Caller is responsible for storing the returned token (e.g. document.settings, OfficeRuntime.storage).
 */

/** REST API session probe (same auth as data endpoints). */
export const SESSION_WHOAMI_PATH = "/api/admin/auth/whoami"

export type TokenResponse = {
	access_token: string
	token_type?: string
	expires_in?: number
	[key: string]: unknown
}

/**
 * Request an access token using the password grant.
 * POST /token with form body; no Authorization header.
 *
 * @param baseUrl - API base URL (e.g. https://api.example.com)
 * @param username - User name
 * @param password - User password
 * @returns Token response with access_token
 */
export async function requestToken(
	baseUrl: string,
	username: string,
	password: string,
): Promise<TokenResponse> {
	const url = `${baseUrl.replace(/\/$/, "")}/token`
	const body = new URLSearchParams({
		grant_type: "password",
		username: username.trim(),
		password: password.trim(),
	})
	const response = await fetch(url, {
		method: "POST",
		headers: { "Content-Type": "application/x-www-form-urlencoded" },
		body: body.toString(),
	})
	if (!response.ok) {
		throw new Error(`Token request failed: ${response.status} ${response.statusText}`)
	}
	const data = (await response.json()) as TokenResponse
	if (!data.access_token) {
		throw new Error("Token response missing access_token")
	}
	return data
}

/** Injected fetch (tests); defaults to global `fetch`. */
export type FetchLike = typeof fetch

/**
 * Returns whether the API still accepts the stored Bearer token.
 * - 200 → true (session valid)
 * - 401 / 403 → false (stale or revoked token; caller should clear storage and show login)
 * - Other failures (5xx, network) → true so a flaky VPN/API does not trap users on login
 */
export async function isStoredAccessTokenAcceptedByApi(
	baseUrl: string,
	token: string,
	fetchFn: FetchLike = fetch,
): Promise<boolean> {
	const url = `${baseUrl.replace(/\/$/, "")}${SESSION_WHOAMI_PATH}`
	try {
		const response = await fetchFn(url, {
			method: "GET",
			headers: { Authorization: `Bearer ${token}` },
		})
		if (response.ok) return true
		if (response.status === 401 || response.status === 403) return false
		return true
	} catch {
		return true
	}
}
